/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Space, Spin, Switch, Table, Tag } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { currentLocale, formatDate } from '../../i18n';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { ImportOutlined, SettingOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import dayjs, { type Dayjs } from 'dayjs';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import PlanEditor from '../../components/grid/PlanEditor';
import { useGetPlotPlanQuery, usePatchPlotPlanMutation } from '../../api/planApi';
import { useGetPlotByIdQuery, useUpdatePlotMutation, useDeletePlotMutation } from '../../api/fieldsApi';
import { useGetPlantingsQuery } from '../../api/plantingsApi';
import { useGetPlotGridViewQuery } from '../../api/bulkOperationsApi';
import { useGetSaveRuntimeSessionQuery } from '../../api/simulationApi';
import { useGetScenarioTemplateQuery, useMoveSaveCursorMutation } from '../../api/savesV2Api';
import type { PlanCellKind, PlanOperation, PlotPlan, PlanBedDeletionConflict, PlanCoordinateConflict } from '../../types/plan';
import type { BedPlantingInfo, BedPlantingItem, PlanLayerVisibility } from '../../types/planManagement';
import type { PlotGridLayers, PlotGridHealthLayerItem } from '../../types/bulkOperations';
import { planCellKey } from '../../utils/planCells';
import { eventOpenDay } from '../../utils/openDayDate';
import { getConflictWorld } from '../../utils/planCoords';
import { isBedDeletionConflict, isCoordinateConflict } from '../../utils/planConflicts';
import { formatBedDisplayName } from '../../utils/bedDisplayName';
import { toastError, toastSuccess } from '../../utils/toast';
import { hasAction } from '../../types/permissions';
import { validateContiguity } from '../../utils/contiguity';
import ActionWizard from '../../components/events/ActionWizard';
import type { ActionTarget } from '../../components/events/ActionWizard.types';
import { useCreatePlantingEventMutation } from '../../api/plantingEventsApi';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import type { Plot } from '../../models/Plot';
import { withPlantingRuntime } from '../../utils/scenarioWorldAdapters';
import { buildPlotPlanFromWorld } from '../../utils/worldPlanAdapters';

const styles = {
  plotSubtitle: css({ marginTop: 8, marginBottom: 16, color: '#666', fontSize: '14px' }),
  bedsSection: css({
    marginTop: 16,
  }),
  bedItem: css({
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  }),
  bedDetails: css({
    marginTop: 8,
    color: '#666',
    fontSize: 14,
  }),
  bedSpacing: css({
    marginRight: 16,
  }),
  emptyStateCard: css({
    textAlign: 'center' as const,
    padding: 24,
    color: '#999',
  }),
};

const PlotPlanEditor: React.FC = () => {
  const params = useParams<{ saveId: string; plotId: string; fieldId?: string }>();
  const { saveId } = params;
  const { t } = useTranslation('planning');
  const bedFallbackLabel = useCallback(
    (bedId: string) => t('bedName.withId', { id: bedId.slice(0, 8) }),
    [t],
  );
  const save = useSave();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();
  const plotId = params.plotId; // UUID or storeId
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state: any) => state.auth.user);
  const isScenarioSave = save.type === 'scenario';
  const [showTableView, setShowTableView] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [bedModalOpen, setBedModalOpen] = useState(false);
  const [bedCells, setBedCells] = useState<Array<{ row: number; col: number }>>([]);
  const [conflictKeys, setConflictKeys] = useState<Set<string>>(new Set());
  const [editForm] = Form.useForm();
  const [bedForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [actionWizardOpen, setActionWizardOpen] = useState(false);
  const [actionTargets, setActionTargets] = useState<ActionTarget[]>([]);
  const [scenarioDateTarget, setScenarioDateTarget] = useState<Dayjs | null>(null);
  const [layerVisibility, setLayerVisibility] = useState<PlanLayerVisibility>({
    plantings: true,
    watering: true,
    issues: true,
  });
  const [transplantMode, setTransplantMode] = useState<{
    active: boolean;
    sourceBedId: string | null;
    targets: ActionTarget[];
  }>({ active: false, sourceBedId: null, targets: [] });
  const [updatePlot, { isLoading: updating }] = useUpdatePlotMutation();
  const [deletePlot] = useDeletePlotMutation();
  const [createPlantingEvent] = useCreatePlantingEventMutation();
  const [moveSaveCursor, { isLoading: movingScenarioCursor }] = useMoveSaveCursorMutation();

  const scenarioPlanData = useMemo<PlotPlan | null>(() => {
    if (!isScenarioSave || !plotId) return null;
    return buildPlotPlanFromWorld(world.structure, plotId);
  }, [isScenarioSave, plotId, world.structure]);

  const scenarioPlot = useMemo(() => {
    if (!isScenarioSave || !plotId) return null;
    return world.structure.plots.find((item) => item.id === plotId) ?? null;
  }, [isScenarioSave, plotId, world.structure.plots]);

  const scenarioBeds = useMemo(() => {
    if (!isScenarioSave || !plotId) return [];
    return world.structure.beds.filter((item) => item.plotId === plotId);
  }, [isScenarioSave, plotId, world.structure.beds]);

  const scenarioBedInfoById = useMemo(() => {
    const details = new Map<string, { name?: string | null; area?: number | null; parameters?: Record<string, unknown> | null }>();

    (scenarioPlanData?.beds || []).forEach((bed) => {
      details.set(String(bed.id), {
        name: bed.name ?? null,
        area: bed.area ?? null,
        parameters: bed.parameters || {},
      });
    });

    scenarioBeds.forEach((bed) => {
      const current = details.get(String(bed.id)) || {};
      details.set(String(bed.id), {
        name: current.name ?? bed.name ?? null,
        area: current.area ?? bed.area ?? null,
        parameters: current.parameters ?? bed.parameters ?? {},
      });
    });

    return details;
  }, [scenarioBeds, scenarioPlanData?.beds]);

  const scenarioPlantings = useMemo(() => {
    if (!isScenarioSave || scenarioBeds.length === 0) return [];
    const bedIds = new Set(scenarioBeds.map((bed) => bed.id));
    return world.structure.plantings
      .filter((item) => item.bedId && bedIds.has(item.bedId))
      .map((planting) => withPlantingRuntime(
        planting,
        world.runtime?.plantings?.[String(planting.id)],
      ));
  }, [isScenarioSave, scenarioBeds, world.runtime?.plantings, world.structure.plantings]);

  const scenarioPlotRecord = useMemo<Plot | null>(() => {
    if (!isScenarioSave || !plotId) return null;

    return {
      id: plotId,
      fieldId: scenarioPlot?.fieldId ?? params.fieldId ?? '',
      name: scenarioPlot?.name ?? 'Scenario plot',
      area: scenarioPlot?.area ?? null,
      location_coords: null,
      description: null,
      actions: [],
      beds: Array.from(scenarioBedInfoById.entries()).map(([bedId, details]) => ({
        id: bedId,
        plotId,
        name: details.name ?? null,
        area: details.area ?? null,
      })),
    };
  }, [isScenarioSave, params.fieldId, plotId, scenarioBedInfoById, scenarioPlot]);

  const scenarioPlotPlantings = useMemo(() => {
    return scenarioPlantings.map((planting) => ({
      id: String(planting.id),
      crop: {
        id: String(planting.cropId ?? planting.id),
        name: planting.crop ?? t('plotEditor.cropFallback'),
        latinName: '',
      },
      lifecycleStatus: planting.lifecycleStatus ?? 'active',
      plantingType: planting.quantity && planting.quantity > 1 ? 'batch' : 'single',
      quantity: planting.quantity ?? 1,
      locations: planting.bedId ? [{ bedId: planting.bedId, toDate: null }] : [],
      currentProperties: [
        ...(typeof planting.healthScore === 'number'
          ? [{ propertyCode: 'health_score', valueNumber: planting.healthScore }]
          : []),
        ...(planting.lifecyclePhase
          ? [{ propertyCode: 'lifecycle_phase', valueText: planting.lifecyclePhase }]
          : []),
        ...(typeof planting.lifecycleGateProgressPct === 'number'
          ? [{ propertyCode: 'lifecycle_gate_progress_pct', valueNumber: planting.lifecycleGateProgressPct }]
          : []),
        ...(planting.lifecycleNextPhase
          ? [{ propertyCode: 'lifecycle_next_phase', valueText: planting.lifecycleNextPhase }]
          : []),
        ...(typeof planting.lifecycleYieldMultiplier === 'number'
          ? [{ propertyCode: 'lifecycle_yield_multiplier', valueNumber: planting.lifecycleYieldMultiplier }]
          : []),
      ],
    }));
  }, [scenarioPlantings, t]);

  const scenarioFieldId = scenarioPlotRecord?.fieldId || params.fieldId;
  const scenarioCursorDate = isScenarioSave ? (save.cursor?.simDate || save.cursor?.sim_date || null) : null;
  const refreshPlanView = () => (isScenarioSave ? refreshWorld() : refetch());
  const handleScenarioUnsupportedAction = () => {
    toastError(t('plotEditor.scenarioReadOnly'));
  };
  const handleScenarioViewBed = (bedId: string) => {
    navigate(`/saves/${saveId}/beds/${bedId}`);
  };
  const handleScenarioViewPlanting = (plantingId: string) => {
    navigate(`/saves/${saveId}/plantings/${plantingId}`);
  };
  const handlePlantingCreated = async () => {
    if (isScenarioSave) {
      await refreshWorld();
      return;
    }

    await refreshWorld();
    await Promise.allSettled([refetch(), refetchPlantings(), refetchGridView()]);
  };

  // CN-0012: With UUID-only routes, we may not have farmId in params
  // Get it from plot response instead
  const { data: plotResponse, isLoading: isPlotLoadingFromApi } = useGetPlotByIdQuery(
    { saveId: saveId ?? '', plotId: plotId ?? '' },
    { skip: !saveId || !plotId || isScenarioSave }
  );
  
  const farmId = save.farmId ?? plotResponse?.field?.farmId ?? plotResponse?.field?.farm?.id;

  const { data: scenarioTemplate } = useGetScenarioTemplateQuery(
    {
      templateId: save.scenarioTemplateId || '',
      version: save.scenarioTemplateVersion ?? undefined,
    },
    {
      skip: !isScenarioSave || !save.scenarioTemplateId,
    },
  );

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(saveId ?? '', {
    skip: !saveId || !isAuthenticated || isScenarioSave,
  });
  const scenarioDateBounds = useMemo(() => {
    if (!isScenarioSave) return null;

    const startRaw = scenarioTemplate?.worldSpec?.start_date
      || scenarioTemplate?.worldSpec?.startDate
      || null;

    const startDate = startRaw && dayjs(startRaw).isValid()
      ? dayjs(startRaw).format('YYYY-MM-DD')
      : null;

    const endFromTemplate = startDate && Number.isFinite(Number(scenarioTemplate?.durationDays))
      ? dayjs(startDate).add(Number(scenarioTemplate?.durationDays), 'day').format('YYYY-MM-DD')
      : null;

    const endRaw = endFromTemplate || null;
    const endDate = endRaw && dayjs(endRaw).isValid()
      ? dayjs(endRaw).format('YYYY-MM-DD')
      : null;

    if (!startDate && !endDate) return null;
    return { startDate, endDate };
  }, [isScenarioSave, scenarioTemplate]);
  const simSessionIdForPlan = isScenarioSave
    ? (activeSimSession?.id ?? saveId ?? null)
    : activeSimSession?.id;
  const currentSimDate = isScenarioSave ? null : activeSimSession?.simDate ?? null;

  const scenarioWeatherSummary = useMemo(() => {
    if (!isScenarioSave) {
      return null;
    }

    const weather = world.runtime?.weather;
    if (!weather || typeof weather !== 'object') {
      return null;
    }

    const currentPoint = weather as Record<string, unknown>;
    const precipitationMm = Number(currentPoint.precipitationMm ?? 0) || 0;
    const humidityPct = Number(currentPoint.humidityPct ?? 0) || 0;
    const tempMin = currentPoint.tempMin != null ? Number(currentPoint.tempMin) : null;
    const tempMax = currentPoint.tempMax != null ? Number(currentPoint.tempMax) : null;
    const tempAvg = currentPoint.tempAvg != null
      ? Number(currentPoint.tempAvg)
      : (tempMin != null && tempMax != null ? +(((tempMin + tempMax) / 2).toFixed(1)) : null);
    const daylightHours = currentPoint.astronomicalDaylightHours != null
      ? Number(currentPoint.astronomicalDaylightHours)
      : null;

    if (!currentPoint) return null;

    return {
      currentPoint,
      precipitationMm,
      humidityPct,
      tempAvg,
      daylightHours,
      wateringHint: precipitationMm >= 4 || humidityPct >= 80
        ? t('plotEditor.weather.wetHint')
        : t('plotEditor.weather.dryHint'),
    };
  }, [isScenarioSave, t, world.runtime?.weather]);
  const scenarioCursorAtStart = useMemo(() => {
    if (!scenarioCursorDate || !scenarioDateBounds?.startDate) return false;
    return dayjs(scenarioCursorDate).isSame(dayjs(scenarioDateBounds.startDate), 'day');
  }, [scenarioCursorDate, scenarioDateBounds?.startDate]);
  const scenarioCursorAtEnd = useMemo(() => {
    if (!scenarioCursorDate || !scenarioDateBounds?.endDate) return false;
    return dayjs(scenarioCursorDate).isSame(dayjs(scenarioDateBounds.endDate), 'day');
  }, [scenarioCursorDate, scenarioDateBounds?.endDate]);

  useEffect(() => {
    if (!isScenarioSave) return;
    setScenarioDateTarget(scenarioCursorDate ? dayjs(scenarioCursorDate) : null);
  }, [isScenarioSave, scenarioCursorDate]);

  const isScenarioDateDisabled = (date: Dayjs) => {
    if (!scenarioDateBounds) return false;
    if (scenarioDateBounds.startDate && date.isBefore(dayjs(scenarioDateBounds.startDate), 'day')) {
      return true;
    }
    if (scenarioDateBounds.endDate && date.isAfter(dayjs(scenarioDateBounds.endDate), 'day')) {
      return true;
    }
    return false;
  };

  const openDeepRewindConfirm = (targetDate: string, estimate: {
    days_to_recalculate?: number;
    events_after_cursor?: number;
    invalidated_events_estimate?: number;
    estimated_replay_cost_ms?: number;
  }) => {
    modal.confirm({
      title: t('plotEditor.rewind.title', { date: formatDate(targetDate) }),
      content: (
        <Space direction="vertical" size={4}>
          <span>{t('plotEditor.rewind.replayNeeded')}</span>
          <span>{t('plotEditor.rewind.daysToRecalculate', { value: String(estimate.days_to_recalculate ?? 0) })}</span>
          <span>{t('plotEditor.rewind.eventsAfterCursor', { value: String(estimate.events_after_cursor ?? 0) })}</span>
          <span>{t('plotEditor.rewind.invalidated', { value: String(estimate.invalidated_events_estimate ?? 0) })}</span>
          <span>{t('plotEditor.rewind.replayCost', { value: String(estimate.estimated_replay_cost_ms ?? 0) })}</span>
        </Space>
      ),
      okText: t('plotEditor.rewind.confirm'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        await applyScenarioCursorMove(targetDate, true);
      },
    });
  };

  const applyScenarioCursorMove = async (targetDate: string, confirmDeep = false) => {
    if (!saveId) return;

    try {
      const result = await moveSaveCursor({
        saveId,
        body: {
          sim_date: targetDate,
          expected_save_revision: save.revision,
          ...(confirmDeep ? { confirm_deep_rewind: true } : {}),
        },
      }).unwrap();

      if ('status' in result && result.status === 'requires_confirmation') {
        openDeepRewindConfirm(targetDate, result.estimate);
        return;
      }

      await refreshWorld();
      toastSuccess(t('plotEditor.scenarioDateSet', { date: formatDate(targetDate) }));
    } catch (error) {
      const err = error as {
        data?: {
          message?: string;
          estimate?: {
            days_to_recalculate?: number;
            events_after_cursor?: number;
            invalidated_events_estimate?: number;
            estimated_replay_cost_ms?: number;
          };
          reason?: string;
        };
      };

      if (err.data?.estimate && err.data.reason === 'deep_rewind') {
        openDeepRewindConfirm(targetDate, err.data.estimate);
        return;
      }

      toastError(err.data?.message || error);
    }
  };

  const handleScenarioStepDay = (deltaDays: number) => {
    if (!scenarioCursorDate) return;
    const nextDate = dayjs(scenarioCursorDate).add(deltaDays, 'day');
    if (isScenarioDateDisabled(nextDate)) {
      return;
    }
    void applyScenarioCursorMove(nextDate.format('YYYY-MM-DD'));
  };

  const handleScenarioJumpToDate = () => {
    if (!scenarioDateTarget) {
      toastError(t('plotEditor.selectScenarioDate'));
      return;
    }
    if (isScenarioDateDisabled(scenarioDateTarget)) {
      toastError(t('plotEditor.dateOutOfWindow'));
      return;
    }
    void applyScenarioCursorMove(scenarioDateTarget.format('YYYY-MM-DD'));
  };

  const plot: Plot | undefined = isScenarioSave ? (scenarioPlotRecord ?? undefined) : plotResponse?.plot;
  const actions = plot?.actions || [];
  const canEditPlan = !isScenarioSave && (hasAction(user, 'plots', 'plots.update', farmId) || hasAction(user, 'beds', 'beds.create', farmId));
  const canUpdatePlot = actions.some((a: any) => a.code === 'plots.update');
  const canDeletePlot = actions.some((a: any) => a.code === 'plots.delete');
  const {
    data: planDataFromApi,
    isLoading: isPlanLoadingFromApi,
    refetch,
  } = useGetPlotPlanQuery(
    { saveId: saveId ?? '', plotId: plotId ?? '' },
    { skip: !saveId || !plotId || isScenarioSave }
  );
  const [patchPlan, { isLoading: isPatching }] = usePatchPlotPlanMutation();

  const [localPlan, setLocalPlan] = useState<PlotPlan | null>(null);
  const planData = isScenarioSave ? scenarioPlanData : planDataFromApi;
  const isLoading = isScenarioSave ? false : isPlanLoadingFromApi;
  const isPlotLoading = isScenarioSave ? false : isPlotLoadingFromApi;

  // React Router can reuse this component while only route params change.
  // Never let the anti-race local snapshot from one save/plot mask the
  // canonical topology of the next one.
  useEffect(() => {
    setLocalPlan(null);
    setConflictKeys(new Set());
  }, [saveId, plotId]);

  useEffect(() => {
    if (isScenarioSave || !planDataFromApi) return;
    setLocalPlan((prev) => {
      if (!prev) return planDataFromApi;
      // The PATCH /plan response includes the freshly created bed or
      // structural object, but the SaveV2 world-snapshot refetch triggered
      // by invalidatesTags can race backend snapshot rebuild and return the
      // pre-mutation view. Don't roll back local entities that aren't yet
      // visible to the snapshot — otherwise the new bed / greenhouse /
      // fence disappears until the next patch redraws it.
      const apiBedIds = new Set((planDataFromApi.beds || []).map((b) => b.id));
      for (const bed of prev.beds || []) {
        if (!apiBedIds.has(bed.id)) return prev;
      }
      const apiSoIds = new Set((planDataFromApi.structuralObjects || []).map((s) => s.id));
      for (const so of prev.structuralObjects || []) {
        if (!apiSoIds.has(so.id)) return prev;
      }
      // Painted path cells are not entities with ids, so the bed/SO checks
      // above don't cover them: a freshly painted дорожка lives only in the
      // patch response. Without this, the racing pre-mutation snapshot rolls it
      // back and it stays invisible until the next patch or a reload.
      // (Изгородь — объект, её страхует проверка SO выше.)
      const apiCellKinds = new Map(
        (planDataFromApi.cells || []).map((c) => [planCellKey(c.row, c.col), c.kind]),
      );
      for (const cell of prev.cells || []) {
        if (
          cell.kind === 'path'
          && apiCellKinds.get(planCellKey(cell.row, cell.col)) !== cell.kind
        ) {
          return prev;
        }
      }
      return planDataFromApi;
    });
    setConflictKeys(new Set());
  }, [isScenarioSave, planDataFromApi]);

  const { data: plotPlantingsFromApi = [], refetch: refetchPlantings } = useGetPlantingsQuery(
    saveId && plotId ? { saveId, plotId, simSessionId: simSessionIdForPlan } : undefined,
    { skip: !saveId || !plotId || isScenarioSave }
  );
  const plotPlantings = isScenarioSave ? scenarioPlotPlantings : plotPlantingsFromApi;

  const { data: gridViewWithLayers, isFetching: isFetchingLayers, refetch: refetchGridView } = useGetPlotGridViewQuery(
    plotId && !isScenarioSave
      ? {
          plotId,
          saveId,
          includeEmpty: true,
          layers: ['watering', 'issues'],
          layerWindowDays: 14,
        }
      : skipToken
  );

  const lastSimDateRef = useRef<string | null>(null);

  useEffect(() => {
    if (isScenarioSave) return;
    if (!simSessionIdForPlan || currentSimDate === null) {
      lastSimDateRef.current = currentSimDate;
      return;
    }

    if (lastSimDateRef.current === null) {
      lastSimDateRef.current = currentSimDate;
      return;
    }

    if (currentSimDate === lastSimDateRef.current) {
      return;
    }

    lastSimDateRef.current = currentSimDate;
    void Promise.allSettled([refetchPlantings(), refetchGridView(), refetch()]);
  }, [isScenarioSave, simSessionIdForPlan, currentSimDate, refetchPlantings, refetchGridView, refetch]);

  const effectivePlan = isScenarioSave ? scenarioPlanData : (localPlan || planData);

  // CN-0015: Build bedId→BedPlantingInfo map from canonical /plan + /plantings data
  // CN-0015: Convert world coordinates to bed label (e.g., row=1, col=4 → "A1B4")
  const worldToLabel = (row: number, col: number): string => {
    const rowAxis = row >= 1 ? 'A' : 'C';
    const rowLabel = row >= 1 ? row : -row;
    const colAxis = col >= 1 ? 'B' : 'D';
    const colLabel = col >= 1 ? col : -col;
    return `${rowAxis}${rowLabel}${colAxis}${colLabel}`;
  };

  const bedPlantingMap = useMemo(() => {
    const map = new Map<string, BedPlantingInfo>();
    const allPlanCells = effectivePlan?.cells || [];
    const areaByBedId = new Map<string, number>();
    const customBedNameById = new Map<string, string | null>();
    (plot?.beds || []).forEach((bed: any) => {
      if (bed?.id && bed?.area != null) {
        areaByBedId.set(String(bed.id), Number(bed.area));
      }
      if (bed?.id) {
        customBedNameById.set(String(bed.id), typeof bed?.name === 'string' ? bed.name : null);
      }
    });

    // Stable bed names from plan cells (works immediately after createBed and for negative coords)
    const bedNameById = new Map<string, string>();
    const bedAnchorById = new Map<string, { row: number; col: number }>();
    allPlanCells.forEach((c) => {
      if (c.kind !== 'bed' || !c.bedId) return;
      const current = bedAnchorById.get(c.bedId);
      if (!current || c.row < current.row || (c.row === current.row && c.col < current.col)) {
        bedAnchorById.set(c.bedId, { row: c.row, col: c.col });
      }
    });
    bedAnchorById.forEach((anchor, bedId) => {
      const anchorCode = worldToLabel(anchor.row, anchor.col);
      bedNameById.set(bedId, formatBedDisplayName(customBedNameById.get(bedId), anchorCode, bedId));
    });

    // Populate all beds from plan cells
    allPlanCells.forEach((c) => {
      if (c.kind === 'bed' && c.bedId && !map.has(c.bedId)) {
        map.set(c.bedId, {
          bedId: c.bedId,
          bedName: bedNameById.get(c.bedId) || bedFallbackLabel(c.bedId),
          area: areaByBedId.get(c.bedId),
          plantings: [],
        });
      }
    });

    // Enrich from plantings API by active bed locations
    plotPlantings.forEach((planting) => {
      const activeLocations = (planting.locations || []).filter((loc) => !loc.toDate);
      activeLocations.forEach((loc) => {
        const bedId = String(loc.bedId);
        const existing = map.get(bedId) || {
          bedId,
          bedName: bedNameById.get(bedId) || bedFallbackLabel(bedId),
          area: areaByBedId.get(bedId),
          plantings: [],
        };

        const plantingId = String(planting.id);
        if (!existing.plantings.some((p) => p.id === plantingId)) {
          // In simulation mode, override lifecycleStatus based on sim-computed vegetation_stage
          let effectiveStatus = planting.lifecycleStatus || 'active';
          const lifecyclePhaseProp = planting.currentProperties?.find((p: any) => p.propertyCode === 'lifecycle_phase');
          const lifecycleGateProgressProp = planting.currentProperties?.find((p: any) => p.propertyCode === 'lifecycle_gate_progress_pct');
          const lifecycleNextPhaseProp = planting.currentProperties?.find((p: any) => p.propertyCode === 'lifecycle_next_phase');
          const lifecycleYieldMultiplierProp = planting.currentProperties?.find((p: any) => p.propertyCode === 'lifecycle_yield_multiplier');
          if (simSessionIdForPlan && planting.currentProperties) {
            const vegStage = planting.currentProperties.find(
              (p) => p.propertyCode === 'vegetation_stage'
            );
            if (vegStage?.valueText === 'dead') {
              effectiveStatus = 'dead';
            } else if (vegStage?.valueText === 'transferred') {
              effectiveStatus = 'transferred';
            }
          }

          existing.plantings.push({
            id: plantingId,
            cropId: String(planting.crop.id),
            cropName: planting.crop.name,
            cropLatinName: planting.crop.latinName,
            lifecycleStatus: effectiveStatus,
            lifecyclePhase: lifecyclePhaseProp?.valueText ?? null,
            lifecycleGateProgressPct: lifecycleGateProgressProp?.valueNumber ?? null,
            lifecycleNextPhase: lifecycleNextPhaseProp?.valueText ?? null,
            lifecycleYieldMultiplier: lifecycleYieldMultiplierProp?.valueNumber ?? null,
            plantingType: planting.plantingType,
            quantity: planting.quantity,
            autoHarvest: planting.autoHarvest,
          });
        }

        map.set(bedId, existing);
      });
    });

    return map;
  }, [bedFallbackLabel, effectivePlan, plotPlantings, plot?.beds, simSessionIdForPlan]);

  // Extract beds from cells for table view
  const beds = useMemo(() => {
    return Array.from(bedPlantingMap.values())
      .map((bed) => ({
        id: bed.bedId,
        name: bed.bedName || bedFallbackLabel(bed.bedId),
        plantings: bed.plantings.filter((p) => p.lifecycleStatus === 'active'),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, currentLocale()));
  }, [bedFallbackLabel, bedPlantingMap]);

  // Build health layer from plantings' currentProperties (per bed: worst health, representative stage)
  const enrichedLayerData = useMemo<PlotGridLayers | undefined>(() => {
    const base = gridViewWithLayers?.layers;
    const healthByBed: Record<string, PlotGridHealthLayerItem> = {};

    plotPlantings.forEach((planting: any) => {
      const isDead = String(planting.lifecycleStatus || '').toLowerCase() === 'dead';
      const props = planting.currentProperties;
      // Always process dead plantings even if currentProperties is empty
      if (!isDead && (!Array.isArray(props) || props.length === 0)) return;

      const healthProp = Array.isArray(props) ? props.find((p: any) => p.propertyCode === 'health_score') : undefined;
      const stageProp = Array.isArray(props) ? props.find((p: any) => p.propertyCode === 'vegetation_stage') : undefined;
      const diseaseProp = Array.isArray(props) ? props.find((p: any) => p.propertyCode === 'disease_summary') : undefined;
      const healthScore = isDead ? 0 : (healthProp?.valueNumber ?? null);
      const vegetationStage = stageProp?.valueText ?? null;

      // Parse disease_summary JSON text → array of active diseases
      let plantingDiseases: Array<{ code: string; severity: number }> = [];
      if (diseaseProp?.valueText) {
        try { plantingDiseases = JSON.parse(diseaseProp.valueText); } catch { /* ignore */ }
      }

      const activeLocations = (planting.locations || []).filter((loc: any) => !loc.toDate);
      activeLocations.forEach((loc: any) => {
        const bedId = String(loc.bedId);
        const existing = healthByBed[bedId];
        // Keep worst health score per bed
        if (!existing || (healthScore !== null && (existing.healthScore === null || healthScore < existing.healthScore))) {
          const score = healthScore;
          const status = isDead ? 'dead' as const
            : score === null ? 'unknown' as const
            : score >= 80 ? 'excellent' as const
            : score >= 60 ? 'good' as const
            : score >= 40 ? 'fair' as const
            : score >= 20 ? 'poor' as const
            : 'critical' as const;
          healthByBed[bedId] = { bedId, healthScore: score, vegetationStage, status, activeDiseases: plantingDiseases };
        } else if (plantingDiseases.length > 0) {
          // Merge diseases from multiple plantings in the same bed
          const merged = [...(existing.activeDiseases || [])];
          for (const d of plantingDiseases) {
            const idx = merged.findIndex(m => m.code === d.code);
            if (idx >= 0) {
              if (d.severity > merged[idx].severity) merged[idx] = d;
            } else {
              merged.push(d);
            }
          }
          existing.activeDiseases = merged;
        }
      });
    });

    const wateringByBed = { ...(base?.watering?.byBed || {}) } as Record<string, any>;

    const hasHealth = Object.keys(healthByBed).length > 0;
    const hasWatering = Object.keys(wateringByBed).length > 0;
    if (!base && !hasHealth && !hasWatering) return undefined;

    return {
      ...base,
      ...(hasWatering ? { watering: { ...(base?.watering || {}), byBed: wateringByBed } } : {}),
      ...(hasHealth ? { health: { byBed: healthByBed } } : {}),
    };
  }, [gridViewWithLayers?.layers, plotPlantings]);

  const handlePatchError = (error: any) => {
    const status = error?.status ?? error?.originalStatus;
    if (status === 403) {
      toastError(t('fieldEditor.planLimit'));
      return;
    }
    if (status === 409) {
      const conflicts = error?.data?.conflicts || [];

      // Distinguish between bed deletion conflicts and coordinate conflicts
      const bedDeletionConflicts = conflicts.filter(isBedDeletionConflict);
      const coordinateConflicts = conflicts.filter(isCoordinateConflict);

      if (bedDeletionConflicts.length > 0) {
        handleBedDeletionConflicts(bedDeletionConflicts);
      } else if (coordinateConflicts.length > 0) {
        handleCoordinateConflicts(coordinateConflicts);
      }
      return;
    }
    toastError(error);
  };

  const handlePatch = async (operations: PlanOperation[]) => {
    if (!plotId) return;
    if (isScenarioSave) {
      handleScenarioUnsupportedAction();
      return;
    }
    try {
      const updated = await patchPlan({ saveId: saveId || '', plotId, operations }).unwrap();
      setLocalPlan(updated);
      setConflictKeys(new Set());
      // Refetch grid view because bulkOperationsApi is a separate createApi instance
      // and cannot be invalidated by patchPlotPlan (which lives in apiSlice).
      // Plan changes (new beds, modifiers) affect the watering/issues layer.
      await Promise.allSettled([refetch(), refetchPlantings(), refetchGridView()]);
    } catch (error: any) {
      handlePatchError(error);
    }
  };

  const handleBedDeletionConflicts = (conflicts: PlanBedDeletionConflict[]) => {
    const totalPlantings = conflicts.reduce((sum, c) => sum + c.activePlantings, 0);
    const bedIds = conflicts.map(c => c.bedId);
    
    Modal.error({
      title: t('plotPage.bedDeletion.title'),
      content: (
        <div>
          <p>
            {conflicts.length === 1
              ? t('plotPage.bedDeletion.single', { bed: bedIds[0], count: totalPlantings })
              : t('plotPage.bedDeletion.multiple', { beds: bedIds.join(', '), count: totalPlantings })}
          </p>
          <p style={{ marginTop: 12, color: '#666', fontSize: '14px' }}>
            {t('plotPage.bedDeletion.before')}
          </p>
          <ul style={{ marginTop: 8, paddingLeft: 20, color: '#666', fontSize: '14px' }}>
            <li>{t('plotPage.bedDeletion.finishLifecycle')}</li>
            <li>{t('plotPage.bedDeletion.transplant')}</li>
          </ul>
        </div>
      ),
      okText: bedIds.length === 1 ? t('plotPage.bedDeletion.openBed') : t('common:actions.close'),
      onOk: () => {
        if (bedIds.length === 1) {
          navigate(`/saves/${saveId}/beds/${bedIds[0]}`);
        }
      },
    });
  };

  const handleCoordinateConflicts = (conflicts: PlanCoordinateConflict[]) => {
    if (!localPlan || !localPlan.boundingBox) return;
    const newKeys = new Set<string>();

    conflicts.forEach((conflict) => {
      const world = getConflictWorld(conflict);
      if (!world) return;
      // CN-0012: Use global coordinates directly
      newKeys.add(planCellKey(world.rowWorld, world.colWorld));
    });

    setConflictKeys(newKeys);
    
    Modal.error({
      title: t('plotPage.bedConflict.title'),
      content: t('plotPage.bedConflict.content'),
      okText: t('common:actions.close'),
    });
  };

  const handleOpenSettings = () => {
    if (!plot) return;
    editForm.setFieldsValue({
      name: plot.name,
      area: plot.area ?? undefined,
      location_coords: plot.location_coords ?? undefined,
      description: plot.description ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!plot || !plotId) return;
    try {
      const values = await editForm.validateFields();
      await updatePlot({ saveId: saveId || '', id: String(plot.id), payload: values }).unwrap();
      setEditOpen(false);
      refetch();
      toastSuccess(t('plotEditor.toasts.plotUpdated'));
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteFromModal = () => {
    if (!plot || !plotId) return;
    setEditOpen(false);
    modal.confirm({
      title: t('fieldPage.deletePlotConfirm'),
      content: t('plotEditor.deletePlotContent'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          const actualFieldId = params.fieldId || plotResponse?.plot?.fieldId;
          await deletePlot({ saveId: saveId || '', plotId: String(plot.id), fieldId: actualFieldId ? String(actualFieldId) : undefined }).unwrap();
          toastSuccess(t('plotEditor.toasts.plotDeleted'));
          navigate(actualFieldId ? `/saves/${saveId}/fields/${actualFieldId}/plan` : `/saves/${saveId}`);
        } catch (error) {
          toastError(error);
        }
      },
    });
  };

  const handleBedSelection = (cells: Array<{ row: number; col: number }>) => {
    if (!canEditPlan) return;
    setBedCells(cells);
    setBedModalOpen(true);
  };

  const handleCreateBed = async () => {
    if (!bedCells.length) return;
    
    // Validate contiguity
    const contiguityError = validateContiguity(bedCells, t('plotPage.bedEntity'));
    if (contiguityError) {
      toastError(contiguityError);
      return;
    }
    
    try {
      const values = await bedForm.validateFields();
      const bedPayload = Object.keys(values).reduce<Record<string, unknown>>((acc, key) => {
        if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
          acc[key] = values[key];
        }
        return acc;
      }, {});

      const cellByKey = new Map((localPlan?.cells || planData?.cells || []).map((cell) => [planCellKey(cell.row, cell.col), cell]));
      const pathCellsToClear = bedCells.filter((cell) => {
        const existing = cellByKey.get(planCellKey(cell.row, cell.col));
        return existing?.kind === 'path';
      });

      const operations: PlanOperation[] = [];
      if (pathCellsToClear.length > 0) {
        operations.push({
          op: 'paintCells',
          cells: pathCellsToClear.map((cell) => ({ ...cell, kind: 'empty' })),
        });
      }

      operations.push({
        op: 'createBed',
        cells: bedCells,
        bed: bedPayload,
      });

      const updated = await patchPlan({
        saveId: saveId || '',
        plotId,
        operations,
      }).unwrap();

      setLocalPlan(updated);
      setConflictKeys(new Set());
      toastSuccess(t('plotPage.bedCreated'));
      bedForm.resetFields();
      setBedCells([]);
      setBedModalOpen(false);
    } catch (error) {
      handlePatchError(error);
    }
  };

  // CN-0015: Batch action from plan grid - selected beds → ActionWizard
  const handleBatchAction = (bedIds: string[]) => {
    if (!bedIds.length) return;
    const targets: ActionTarget[] = bedIds.map(bedId => ({
      bedId,
      bedLabel: bedPlantingMap.get(bedId)?.bedName || bedFallbackLabel(bedId),
    }));
    setActionTargets(targets);
    setActionWizardOpen(true);
  };

  // CN-0015: Management mode — bed action (opens ActionWizard with bed targets)
  const handleManagementBedAction = (bedIds: string[]) => {
    if (!bedIds.length) return;
    const targets: ActionTarget[] = bedIds.map((bedId) => ({
      bedId,
      bedLabel: bedPlantingMap.get(bedId)?.bedName || bedFallbackLabel(bedId),
    }));
    setActionTargets(targets);
    setActionWizardOpen(true);
  };

  // CN-0015: Management mode — planting action (opens ActionWizard with planting targets)
  const handleManagementPlantingAction = (targets: Array<{ plantingId?: string; bedId?: string }>) => {
    if (!targets.length) return;
    const actionTargets: ActionTarget[] = targets.map((t) => {
      const bedInfo = t.bedId ? bedPlantingMap.get(t.bedId) : undefined;
      const planting = bedInfo?.plantings.find((p) => p.id === t.plantingId);
      return {
        plantingId: t.plantingId,
        bedId: t.bedId,
        plotId,
        bedLabel: bedInfo?.bedName || (t.bedId ? bedFallbackLabel(t.bedId) : undefined),
        label: planting?.cropName,
      };
    });
    setActionTargets(actionTargets);
    setActionWizardOpen(true);
  };

  const handleStartTransplantSelection = (params: {
    targets: ActionTarget[];
    sourceBedId: string;
    sourcePlotId: string;
  }) => {
    if (params.sourcePlotId !== plotId) {
      toastError(t('plotEditor.transplant.samePlotOnly'));
      return;
    }

    setTransplantMode({
      active: true,
      sourceBedId: params.sourceBedId,
      targets: params.targets,
    });
    setActionWizardOpen(false);
    setActionTargets([]);
    toastSuccess(t('plotEditor.transplant.activated'));
  };

  const handleCancelTransplantMode = () => {
    setTransplantMode({ active: false, sourceBedId: null, targets: [] });
  };

  const handleTransplantTargetBedClick = async (targetBedId: string) => {
    if (!transplantMode.active) return;

    if (transplantMode.sourceBedId && targetBedId === transplantMode.sourceBedId) {
      toastError(t('plotEditor.transplant.sameBed'));
      return;
    }

    try {
      const activeSimId = activeSimSession?.id ?? null;
      // Перенос датируется открытым днём D+1: дни ≤ курсора посчитаны и закрыты.
      const fromDate = (activeSimId ? eventOpenDay(activeSimSession?.simDate) : null)
        ?? new Date().toISOString().slice(0, 10);
      const plantingTargets = transplantMode.targets.filter((t) => t.plantingId);

      for (const target of plantingTargets) {
        await createPlantingEvent({
          saveId: saveId || '',
          plantingId: String(target.plantingId),
          payload: {
            kind: 'transplant',
            ...(activeSimId ? { simSessionId: activeSimId } : {}),
            ...(activeSimId ? {} : { eventDate: fromDate }),
            properties: {
              transplanting: JSON.stringify({
                bedId: targetBedId,
                fromDate,
              }),
            },
          },
        }).unwrap();
      }

      toastSuccess(t('plotEditor.transplant.done', { count: plantingTargets.length }));
      handleCancelTransplantMode();
      // Plan grid renders plantings from WorldContext, so refresh the world
      // snapshot too — refetch()/refetchPlantings() alone won't move the card.
      await Promise.allSettled([refreshWorld(), refetch(), refetchPlantings()]);
    } catch (error) {
      toastError(error);
    }
  };

  const plotName = plot?.name || plotId;
  const plotArea = plot?.area ? ` (${t('fieldEditor.areaHa', { area: plot.area })})` : '';

  const paintKinds = useMemo<PlanCellKind[]>(() => ['path'], []);

  if (!plotId) {
    return (
      <MainLayout>
        <Card>{t('plotEditor.noPlotId')}</Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {contextHolder}
      <h2>{t('plotPage.title', { name: plotName })}{plotArea}</h2>
      {plot?.description && <div css={styles.plotSubtitle}>{plot.description}</div>}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space wrap>
            <Button onClick={() => navigate(-1)}>{t('common:actions.back')}</Button>
            <Button onClick={() => { void refreshPlanView(); }}>{t('common:actions.refresh')}</Button>
            {(canUpdatePlot || canDeletePlot) && (
              <Button
                icon={<SettingOutlined />}
                onClick={handleOpenSettings}
              >
                {t('fieldEditor.settings')}
              </Button>
            )}
            <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{t('fieldEditor.tableView')}</span>
              <Switch checked={showTableView} onChange={setShowTableView} />
            </div>
            {isScenarioSave && (
              <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Tag color="gold">{t('plotEditor.scenarioDay', { date: scenarioCursorDate ? formatDate(scenarioCursorDate) : '—' })}</Tag>
                {scenarioDateBounds?.startDate && scenarioDateBounds?.endDate ? (
                  <Tag color="blue">
                    {t('plotEditor.window', { from: formatDate(scenarioDateBounds.startDate, 'dayMonthShort'), to: formatDate(scenarioDateBounds.endDate, 'dayMonthShort') })}
                  </Tag>
                ) : null}
                <Button onClick={() => handleScenarioStepDay(-1)} disabled={!scenarioCursorDate || scenarioCursorAtStart} loading={movingScenarioCursor}>
                  {t('plotEditor.dayBack')}
                </Button>
                <Button onClick={() => handleScenarioStepDay(1)} disabled={!scenarioCursorDate || scenarioCursorAtEnd} loading={movingScenarioCursor}>
                  {t('plotEditor.dayForward')}
                </Button>
                <DatePicker
                  value={scenarioDateTarget}
                  onChange={setScenarioDateTarget}
                  disabledDate={isScenarioDateDisabled}
                  allowClear={false}
                />
                <Button
                  type="primary"
                  onClick={handleScenarioJumpToDate}
                  disabled={!scenarioDateTarget || scenarioDateTarget.format('YYYY-MM-DD') === scenarioCursorDate}
                  loading={movingScenarioCursor}
                >
                  {t('plotEditor.goToDay')}
                </Button>
              </div>
            )}
          </Space>
        </Card>

        {!showTableView && (
          <>
            {isScenarioSave && (
              <Alert
                type="info"
                showIcon
                message={t('plotEditor.scenarioPlan')}
                description={`${t('plotEditor.scenarioReadOnly')}${scenarioCursorDate ? ` ${t('plotEditor.scenarioCurrentDate', { date: formatDate(scenarioCursorDate) })}` : ''}`}
              />
            )}
            {isScenarioSave && (
              <Card size="small">
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Space wrap>
                    <Tag color="blue">{t('plotEditor.weather.context')}</Tag>
                    <Tag>{scenarioWeatherSummary ? 'world.runtime.weather' : t('plotEditor.weather.noSnapshot')}</Tag>
                    {scenarioCursorDate ? <Tag color="gold">{t('plotEditor.date', { date: formatDate(scenarioCursorDate) })}</Tag> : null}
                    {scenarioDateBounds?.startDate && scenarioDateBounds?.endDate ? (
                      <Tag color="geekblue">
                        {t('plotEditor.windowOnly', { from: formatDate(scenarioDateBounds.startDate, 'dayMonthShort'), to: formatDate(scenarioDateBounds.endDate, 'dayMonthShort') })}
                      </Tag>
                    ) : null}
                  </Space>
                  {scenarioWeatherSummary ? (
                    <>
                      <Space wrap>
                        <Tag color="cyan">{t('plotEditor.weather.precipitation', { value: scenarioWeatherSummary.precipitationMm })}</Tag>
                        {scenarioWeatherSummary.humidityPct > 0 ? (
                          <Tag color="green">{t('plotEditor.weather.humidity', { value: scenarioWeatherSummary.humidityPct })}</Tag>
                        ) : null}
                        {scenarioWeatherSummary.tempAvg != null ? (
                          <Tag color="orange">{t('plotEditor.weather.tempAvg', { value: scenarioWeatherSummary.tempAvg })}</Tag>
                        ) : null}
                        {scenarioWeatherSummary.daylightHours != null ? (
                          <Tag color="purple">{t('plotEditor.weather.daylight', { value: scenarioWeatherSummary.daylightHours })}</Tag>
                        ) : null}
                      </Space>
                      <Alert
                        type="info"
                        showIcon
                        message={t('plotEditor.weather.layerTitle')}
                        description={`${scenarioWeatherSummary.wateringHint} ${t('plotEditor.weather.source')}`}
                      />
                    </>
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message={t('plotEditor.weather.layerTitle')}
                      description={t('plotEditor.weather.missing')}
                    />
                  )}
                </Space>
              </Card>
            )}
            {transplantMode.active && (
              <Alert
                type="info"
                showIcon
                icon={<ImportOutlined />}
                message={t('plotEditor.transplant.modeTitle')}
                description={t('plotEditor.transplant.modeDescription')}
                action={
                  <Button size="small" onClick={handleCancelTransplantMode}>
                    {t('common:actions.cancel')}
                  </Button>
                }
                style={{ marginBottom: 12 }}
              />
            )}
            {!isScenarioSave && (
              <Space style={{ marginBottom: 12 }}>
                <span>{t('plotEditor.plantingsLayer')}</span>
                <Switch
                  checked={layerVisibility.plantings}
                  onChange={(plantings) => setLayerVisibility((current) => ({ ...current, plantings }))}
                />
              </Space>
            )}
            {(!isScenarioSave && ((isLoading && farmId) || isPlotLoading || !farmId)) ? (
              <Card>
                <Spin />
              </Card>
            ) : !planData ? (
              <Card>
                <div>{isScenarioSave ? t('plotEditor.errors.noScenarioLayout') : t('plotEditor.errors.loadFailed')}</div>
                <Button onClick={() => { void refreshPlanView(); }}>{t('common:actions.retry')}</Button>
              </Card>
            ) : (
              <PlanEditor
                boundingBox={effectivePlan?.boundingBox || planData.boundingBox}
                allowedBoundingBox={effectivePlan?.allowedBoundingBox || planData.allowedBoundingBox}
                globalGrid={effectivePlan?.globalGrid || planData.globalGrid}
                cells={effectivePlan?.cells || planData.cells}
                structuralObjects={effectivePlan?.structuralObjects || planData.structuralObjects}
                beds={effectivePlan?.beds || planData.beds}
                initialMode={isScenarioSave ? 'management' : ((effectivePlan?.beds || planData.beds || []).length > 0 ? 'management' : 'planning')}
                paintKinds={paintKinds}
                saveId={saveId || ''}
                farmId={farmId ?? ''}
                fieldId={scenarioFieldId || plotResponse?.plot?.fieldId}
                plotId={plotId}
                allowBedSelection={!isScenarioSave && canEditPlan}
                disablePlanningMode={isScenarioSave}
                disablePlantingMode={isScenarioSave}
                isBusy={isScenarioSave ? false : isPatching}
                highlightCells={conflictKeys}
                bedPlantingMap={bedPlantingMap}
                onPatch={handlePatch}
                onBedSelection={handleBedSelection}
                onBatchAction={handleBatchAction}
                onManagementBedAction={handleManagementBedAction}
                canRunManagementBedAction={isScenarioSave
                  ? (bedId) => (world.allowedActions.byTarget[`bed:${bedId}`] || []).length > 0
                  : undefined}
                onManagementBedClick={transplantMode.active ? handleTransplantTargetBedClick : undefined}
                onManagementPlantingAction={handleManagementPlantingAction}
                canRunManagementPlantingAction={isScenarioSave
                  ? (plantingId) => (world.allowedActions.byTarget[`planting:${plantingId}`] || []).length > 0
                  : undefined}
                allowManagementAddPlanting={!isScenarioSave}
                allowManagementAutomation={!isScenarioSave}
                onManagementAddPlanting={isScenarioSave ? handleScenarioUnsupportedAction : undefined}
                onManagementViewBed={isScenarioSave ? handleScenarioViewBed : undefined}
                onManagementViewPlanting={isScenarioSave ? handleScenarioViewPlanting : undefined}
                onPlantingCreated={handlePlantingCreated}
                managementTransplantMode={transplantMode.active}
                layerData={enrichedLayerData}
                layerVisibility={isScenarioSave ? undefined : layerVisibility}
                onLayerVisibilityChange={isScenarioSave ? undefined : setLayerVisibility}
                layerDataLoading={isScenarioSave ? false : isFetchingLayers}
                simSessionId={isScenarioSave ? saveId || null : activeSimSession?.id}
                simDate={isScenarioSave
                  ? (save.cursor?.simDate || save.cursor?.sim_date || null)
                  : (activeSimSession?.simDate || null)}
              />
            )}
          </>
        )}

        {showTableView && beds.length > 0 && (
          <div css={styles.bedsSection}>
            <h3>{t('plotPage.beds')}</h3>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={beds}
              onRow={(record: any) => ({
                onClick: () => navigate(`/saves/${saveId}/beds/${record.id}`),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: t('plotPage.columns.bed'),
                  dataIndex: 'name',
                  key: 'name',
                  width: '45%',
                  render: (name: string) => <strong>{name}</strong>,
                },
                {
                  title: t('plotPage.columns.plantings'),
                  key: 'plantings',
                  render: (_: unknown, record: { plantings: BedPlantingItem[] }) => {
                    if (!record.plantings.length) {
                      return <span style={{ color: '#8c8c8c' }}>{t('entityPanel.empty')}</span>;
                    }
                    return (
                      <Space wrap>
                        {record.plantings.map((p) => (
                          <Tag key={p.id} color="green">
                            {p.cropName}
                          </Tag>
                        ))}
                      </Space>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        {showTableView && beds.length === 0 && (
          <Card css={styles.emptyStateCard}>
            {t('plotEditor.noBeds')}
          </Card>
        )}

      <Modal
        title={t('plotPage.settingsTitle')}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditOpen(false)}>
            {t('common:actions.cancel')}
          </Button>,
          canDeletePlot && (
            <Button key="delete" danger onClick={handleDeleteFromModal}>
              {t('fieldPage.deletePlot')}
            </Button>
          ),
          <Button key="save" type="primary" loading={updating} onClick={handleSaveEdit}>
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label={t('fieldPage.form.name')} name="name" rules={[{ required: true, message: t('fieldPage.form.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.areaHa')} name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.coords')} name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('plotPage.createBed')}
        open={bedModalOpen}
        onOk={handleCreateBed}
        onCancel={() => {
          setBedModalOpen(false);
          setBedCells([]);
        }}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={isPatching}
      >
        <Form form={bedForm} layout="vertical">
          <Form.Item label={t('quickAddBed.area')} name="area">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('plotPage.sizeCode')} name="size_cm_encoded">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('plotPage.geoLocation')} name="geo_location">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* CN-0015: Action Wizard for batch actions from plan grid */}
      {(farmId || isScenarioSave) && (
        <ActionWizard
          open={actionWizardOpen}
          onClose={() => {
            setActionWizardOpen(false);
            setActionTargets([]);
          }}
          targets={actionTargets}
          farmId={farmId ?? ''}
          onStartTransplantSelection={handleStartTransplantSelection}
          onSuccess={() => {
            setActionWizardOpen(false);
            setActionTargets([]);
            // Plan grid renders plantings from WorldContext, so the world
            // snapshot must be refreshed (e.g. after "move to storage") — the
            // plot/plantings refetches alone don't update the plan cards.
            void refreshWorld();
            if (isScenarioSave) return;
            void refetch();
            void refetchPlantings();
          }}
          contextLabel={t('plotEditor.actionContext', { name: plotName, count: actionTargets.length })}
          simSessionId={isScenarioSave ? (activeSimSession?.id || saveId || undefined) : activeSimSession?.id}
          simSaveId={isScenarioSave ? (activeSimSession?.saveId || saveId || undefined) : (activeSimSession?.saveId || undefined)}
          simDisplayDate={isScenarioSave
            ? (save.cursor?.sim_date || undefined)
            : (activeSimSession?.simDate || undefined)}
        />
      )}
      </Space>
    </MainLayout>
  );
};

export default PlotPlanEditor;
